import { constants } from '@config';

/**
 * Clears the internal white-spaces from the given string (assumed to be Html)
 * @param {string} domString The given Html as a string
 * @returns The cleared string
 */
function removeDomWhiteSpaces (domString) {
  return domString
    .replace(/>[\t\n\s]+<(?!\/(?:tspan|text))/g, '><')
    .replace(/<\/a>[\t\n\s]+<(\/tspan)/g, '</a></tspan');
}

/**
 * Creates a document object model based on the given string
 * @param {string} svgString The inner Html as string
 */
function stringToDom (svgString) {
  // In order to ignore differences arisen by white-spaces, whipe them all
  let dom = document.createElement('div');
  dom.innerHTML = removeDomWhiteSpaces(svgString);
  dom = restoreWhiteSpaceBlankText(dom);
  return dom;
}

function restoreWhiteSpaceBlankText (dom) {
  const blankTexts = Array.from(dom.querySelectorAll('text.blank'));
  blankTexts.forEach(text => {
    const tspan = text.querySelector('tspan:not(.ordered):not(.bullets)');
    if (tspan) {
      if (tspan.innerHTML !== constants.text.noBreakSpace) {
        tspan.innerHTML = constants.text.noBreakSpace;
      }
    } else if (text.innerHTML !== constants.text.noBreakSpace) {
      text.innerHTML = constants.text.noBreakSpace;
    }
  });
  return dom;
}

function removeTextNodes (element) {
  let childRemoved;

  do {
    childRemoved = false;
    [...element.childNodes].forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        element.removeChild(child);
        childRemoved = true;
      }
    });
  } while (childRemoved);
}

function copyTextNodes (elementFrom, elementTo) {
  [...elementFrom.childNodes].forEach(child => child.nodeType === Node.TEXT_NODE && elementTo.prepend(child));
}

function removeUselessChildren (element) {
  let childRemoved;

  do {
    childRemoved = false;
    [...element.children].forEach((child) => {
      if (child.getAttribute('data-useless')) {
        element.removeChild(child);
        childRemoved = true;
      }
    });
  } while (childRemoved);
}

function equalAttributes (elementA, elementB, attributeName) {
  const attribute = elementA.getAttribute(attributeName);
  return attribute && attribute === elementB.getAttribute(attributeName);
}

/**
 * Gets the default attributes exceptions array; we prefer to keep some attributes untouched if possible
 * @param {HTMLElement} elementA
 * @param {HTMLElement} elementB
 * @returns {string[]} The default attributes exceptions
 */
function defaultAttributesExceptions (elementA, elementB) {
  const attributesExceptions = [];
  ['id', 'xlink:href', 'href'].forEach(attributeName => {
    equalAttributes(elementA, elementB, attributeName) && attributesExceptions.push(attributeName);
  });

  // Canvas background needs its style attribute, so do not touch it
  if (elementA.classList.contains('background') || elementB.classList.contains('background')) {
    attributesExceptions.push('style');
  }

  return attributesExceptions;
}

/**
 * Discards all attributes
 * @param {HTMLElement} element
 * @param {string[]} exceptions Array of attributes to ignore
 */
function removeAllAttributes (element, exceptions = ['id']) {
  const attributeNames = [];
  [...element.attributes].forEach(attribute => {
    exceptions.includes(attribute.name) || attributeNames.push(attribute.name);
  });
  attributeNames.forEach(attributeName => element.removeAttribute(attributeName));
}

/**
 * Copies all attributes from an element to another
 * @param {HTMLElement} elementFrom Source element
 * @param {HTMLElement} elementTo Target element
 * @param {string[]} exceptions Array of attributes to ignore
 */
function copyAllAttributes (elementFrom, elementTo, exceptions = ['id']) {
  [...elementFrom.attributes].forEach(attribute => {
    if (exceptions.includes(attribute.name)) return;
    if (attribute.name === 'xlink:href' || attribute.name === 'href') {
      elementTo.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', attribute.value);
    } else {
      elementTo.setAttribute(attribute.name, attribute.value);
    }
  });
}

/**
 * Copies all children from an element to another while trying to keep the existing ones
 * @param {HTMLElement} elementFrom Source element
 * @param {HTMLElement} elementTo Target element
 */
function copyChildren (elementFrom, elementTo) {
  // Mark element nodes to be removed later
  for (let i = 0; i < elementTo.children.length; i++) {
    elementTo.children[i].setAttribute('data-useless', 'true');
  }

  // Remove any text nodes
  removeTextNodes(elementTo);

  for (let i = 0; i < elementFrom.children.length; i++) {
    const childFrom = elementFrom.children[i];
    const childTo = elementTo.children[i];
    // If the receiver has the i-th child, try to keep it
    childTo ? copyExistingChild(childFrom, childTo) : elementTo.appendChild(childFrom.cloneNode(true));
  }

  copyTextNodes(elementFrom, elementTo);
  removeUselessChildren(elementTo);

  function copyExistingChild (childFrom, childTo) {
    if (childTo.tagName === childFrom.tagName) {
      const attributesExceptions = defaultAttributesExceptions(childTo, childFrom);
      removeAllAttributes(childTo, attributesExceptions);
      copyAllAttributes(childFrom, childTo, attributesExceptions);

      removeTextNodes(childTo);

      // Should we go one more level deeper?
      if (childFrom.childNodes.length > 0) {
        copyChildren(childFrom, childTo);
      }

      copyTextNodes(childFrom, childTo);
    } else {
      elementTo.replaceChild(childFrom.cloneNode(true), childTo);
    }
  }
}

/**
 * Selects and returns an element based on the passed query
 * @param {Document|HTMLElement} startElement The parent object; can be document or an HTMLElement
 * @param {string} query
 * @returns The selected element (can be startElement in case the query is invalid)
 */
function querySelector (startElement, query) {
  if (!query) return startElement;

  let element = startElement;
  const queryParts = query.split('/');

  queryParts.forEach(queryPart => {
    if (!element) return;

    let tagName = queryPart;
    let index = 0;

    // tag[@id='literal'] equals tag#literal
    const queryPartWithId = queryPart.match(/(.+)\[@id='(.+)'\]/);
    if (queryPartWithId) {
      tagName = queryPartWithId[1] + '#' + queryPartWithId[2];
    }

    // tag[N] equals tag:nth-child(N-1)
    const queryPartwithNthChild = queryPart.match(/(.+)\[(\d+)\]/);
    if (queryPartwithNthChild) {
      tagName = queryPartwithNthChild[1];
      index = parseInt(queryPartwithNthChild[2]) - 1;
    }

    if (element) {
      if (index === 0) {
        const elements = element.querySelectorAll(tagName);
        const firstElement = element.querySelector(tagName);
        let isFound = false;
        for (let i = 0; i < elements.length; i++) {
          if (elements[i].parentElement.isEqualNode(element)) {
            element = elements[i];
            isFound = true;
            break;
          }
        }
        if (!isFound) element = firstElement;
      } else {
        let k = 0;
        const nodeList = element.querySelectorAll(tagName);
        const children = [...element.children];

        element = null;

        for (let i = 0; i < nodeList.length; i++) {
          if (children.includes(nodeList[i])) {
            if (k === index) {
              element = nodeList[i];
              break;
            }
            k++;
          }
        }
      }
    }
  });

  return element;
}

/**
 * Compares and extracts the differences between the provided history snapshot and the current Svg Dom
 * @param {HTMLElement} targetDOM The expected Dom
 * @returns A difference object
 */
function compareHistorySnapshots (targetDOM) {
  // The current state
  const currentDOM = stringToDom(INKIVE.EditorFacade.editor.getSvgString());
  // Note: INKIVE.DomCompareTools are defined by initUndo()
  // Make a Dom comparison
  const result = INKIVE.DomCompareTools.compare(targetDOM, currentDOM);
  result._diff = result._diff.filter((difference) => {
    const isLinkChange = difference.message.includes('xmlns:xlink');
    const isBleedChange = difference.message.includes('style') || difference.message.includes('class') || difference.message.includes('data-bleed');
    const isParentSvg = difference.node === 'DIV/svg';
    return !(isParentSvg && (isLinkChange || isBleedChange));
  });
  // Report the result as an object
  return INKIVE.DomCompareTools.groupingReporter.getDifferences(result);
}

/**
 * Changes the scene to have the described change without completely replacing its Dom
 * @param {HTMLElement} targetDOM The desired Dom
 * @param {string} changePath A path to the changed element
 */
function rollbackChange (targetDOM, changePath) {
  // Select from scene; start searching from #svgcontent
  let sceneElement = querySelector(document, changePath.replace('DIV/svg', '#svgcontent'));
  // Select from virtual DOM; start searching from svg
  let targetElement = querySelector(targetDOM, changePath.replace('DIV/svg', 'svg'));

  // Both elements should exist and be of the same type
  if (!sceneElement || !targetElement || !sceneElement.tagName || sceneElement.tagName !== targetElement.tagName) return;

  // Updating the whole scene?
  if (sceneElement.tagName === 'svg') {
    const targetDefs = targetElement.querySelectorAll('defs');
    const sceneDefs = sceneElement.querySelectorAll('defs');
    if (targetDefs.length === sceneDefs.length) {
      for (let i = 0; i < targetDefs.length; i++) {
        copyChildren(targetDefs[i], sceneDefs[i]);
      }
    }
    // Don't update the root svg element, start from main group
    copyChildren(targetElement.querySelector('g.main'), sceneElement.querySelector('g.main'));
    // Also update the background if necessary
    targetElement = targetElement.querySelector('g.background');
    sceneElement = sceneElement.querySelector('g.background');
  }

  // Exceptions for attributes;
  const attributesExceptions = defaultAttributesExceptions(sceneElement, targetElement);
  // Clear optional attributes
  removeAllAttributes(sceneElement, attributesExceptions);
  // Change sceneElement to have targetElement's attributes
  copyAllAttributes(targetElement, sceneElement, attributesExceptions);

  const isVideo = sceneElement.parentElement.classList.contains('video');
  if (isVideo) {
    const foreignObject = sceneElement.parentElement.querySelector('foreignObject');
    foreignObject && copyAllAttributes(targetElement, foreignObject, attributesExceptions);
  }

  // Get a copy of children
  copyChildren(targetElement, sceneElement);
}

function rollbackTextMockChange (targetDOM) {
  const sceneElement = document.querySelector('#textmock');
  const targetElement = targetDOM.querySelector('#textmock');
  if (!sceneElement || !targetElement) return;

  copyChildren(targetElement, sceneElement);
}

export default { stringToDom, compareHistorySnapshots, rollbackChange, rollbackTextMockChange };
